import { useCallback, useEffect, useState } from "react";
import { authTenantServiceApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Shared loader/saver for a tenant's configuration document. Backs every
 * Tenant Settings page that persists into tenant_configurations
 * (branding / featureFlags / securityPolicy) through the REAL
 * tis-auth-tenant-service endpoints — no page invents its own storage.
 *
 *   GET   /api/v1/tenants/:id/configuration
 *   PATCH /api/v1/tenants/:id/configuration
 *
 * Returns the raw config plus a `save(patch)` that PATCHes exactly the
 * keys you pass (branding, featureFlags, securityPolicy) and refreshes.
 */
export function useTenantConfig() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!tenantId) return;
    setLoading(true);
    authTenantServiceApi
      .get(`/api/v1/tenants/${tenantId}/configuration`)
      .then((res) => setConfig(res.data))
      .catch((err) => {
        const m = errorMessage(err);
        if (m) setError(m);
      })
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (patch) => {
      setError("");
      // Merge-safe at the TOP LEVEL only. Each admin page holds the full,
      // authoritative state for the featureFlags KEYS it owns (AdminPortals:
      // portals + nav + locale; AdminCustomFields: labels; AdminCompanyProfile:
      // companyProfile; AdminIntegrations: integrations). The PATCH replaces
      // the whole column, so a page saving from slightly stale state would WIPE
      // a sibling page's top-level key (e.g. a nav save dropping an
      // integrations block, or vice-versa). We therefore overlay the caller's
      // featureFlags onto the freshest server value at the TOP LEVEL, so
      // untouched sibling keys survive — but each key the caller DID provide is
      // taken WHOLESALE (not deep-merged), so an intentional deletion inside a
      // key the page owns (e.g. clearing a per-portal colour override via
      // Reset) is preserved instead of being resurrected from the server copy.
      const merged = { ...patch };
      if (patch.featureFlags !== undefined) {
        merged.featureFlags = { ...(config?.featureFlags ?? {}), ...patch.featureFlags };
      }
      if (patch.branding !== undefined) {
        merged.branding = { ...(config?.branding ?? {}), ...patch.branding };
      }
      const res = await authTenantServiceApi.patch(`/api/v1/tenants/${tenantId}/configuration`, merged);
      setConfig(res.data);
      return res.data;
    },
    [tenantId, config],
  );

  return { tenantId, config, loading, error, setError, save, reload: load };
}
